import { Badge, Box, Button, Flex, Progress, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  AgentModule,
  AiSkillDto,
  AiUsageShareDto,
  useGetOrganizationAiSkillsQuery,
  useRequestOrganizationAiSkillAccessMutation,
} from "../../autogen/bff-api";
import { useAiSkillLabels } from "../../common/ai/useAiSkillLabels";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { displayDate } from "../../common/formatting/displayDate";

export const AiSkills = () => {
  const { t } = useTranslation();
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const { data, isLoading, isError, refetch } = useGetOrganizationAiSkillsQuery({ orgId: organizationId });

  if (isLoading) return <Spinner size="sm" />;
  if (isError || !data) {
    return (
      <Stack spacing="2" align="start">
        <Text>{t("The AI skills could not be loaded.")}</Text>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          {t("Try again")}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="6">
      <Stack spacing="1">
        <Text fontWeight="medium">{t("AI skills")}</Text>
        <Text fontSize="sm" color="smMuted">
          {t("The AI skills your organization has, and the ones available as add-ons.")}
        </Text>
      </Stack>
      <FairUse usage={data.usage} />
      <Stack spacing="3">
        {data.skills.map((skill) => (
          <SkillRow key={skill.skill} organizationId={organizationId} skill={skill} />
        ))}
      </Stack>
    </Stack>
  );
};

const FairUse = ({ usage }: { usage: AiUsageShareDto }) => {
  const { t } = useTranslation();
  const percent = usage.fairUsePercent;
  if (percent == null) {
    return (
      <Text fontSize="sm" color="smMuted">
        {t("No AI fair use is set for your organization, so this month's use is not shown as a share of it.")}
      </Text>
    );
  }
  return (
    <Box>
      <Text fontSize="sm">{t("This month your organization has used {{percent}} % of its AI fair use.", { percent })}</Text>
      <Progress
        mt="2"
        size="sm"
        rounded="full"
        bg="smBackgroundTertiary"
        colorScheme={percent >= 100 ? "orange" : "teal"}
        value={Math.min(percent, 100)}
        aria-label={String(t("Share of this month's AI fair use"))}
      />
      <Text mt="2" fontSize="sm" color="smMuted">
        {t("AI is never switched off. Use above the fair use is invoiced.")}
      </Text>
    </Box>
  );
};

const SkillRow = ({ organizationId, skill }: { organizationId: string; skill: AiSkillDto }) => {
  const { t, i18n } = useTranslation();
  const label = useAiSkillLabels()(skill.skill);
  const apiError = useApiError();
  const [requestAccess, { isLoading }] = useRequestOrganizationAiSkillAccessMutation();
  const locale = i18n.language === "en" ? "en" : "nb";

  const ask = async (module: AgentModule) => {
    try {
      await requestAccess({ orgId: organizationId, skill: module }).unwrap();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Flex borderWidth="1px" borderColor="smBorder" rounded="md" p="4" gap="4" align="center" wrap="wrap">
      <Box flex="1" minW="56">
        <Text fontWeight="semibold">{label.name}</Text>
        <Text fontSize="sm" color="smMuted">
          {label.description}
        </Text>
      </Box>
      {skill.included ? (
        <Badge colorScheme="green">{t("On")}</Badge>
      ) : (
        <Stack spacing="2" align={{ base: "start", md: "end" }}>
          <Badge>{t("Available as an add-on")}</Badge>
          {skill.accessRequestedAt ? (
            <Text fontSize="sm" color="smMuted">
              {t("Access requested {{date}}", { date: displayDate(skill.accessRequestedAt, locale) })}
            </Text>
          ) : (
            <Button size="sm" colorScheme="teal" variant="outline" isLoading={isLoading} onClick={() => ask(skill.skill)}>
              {t("Ask for access")}
            </Button>
          )}
        </Stack>
      )}
    </Flex>
  );
};
